import { Capacitor } from '@capacitor/core';

const hasWindow = () => typeof window !== 'undefined';
const hasNavigator = () => typeof navigator !== 'undefined';

export const PLATFORM = Object.freeze({
    WEB: 'web',
    IOS: 'ios',
    ANDROID: 'android'
});

const matchesDisplayMode = (mode) => (
    hasWindow() &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(`(display-mode: ${mode})`).matches
);

export const getPlatform = () => {
    const platform = Capacitor.getPlatform();
    return platform === PLATFORM.IOS || platform === PLATFORM.ANDROID ? platform : PLATFORM.WEB;
};

export const isWeb = () => getPlatform() === PLATFORM.WEB;

export const isNative = () => !isWeb();

export const isStandalone = () => {
    if (!isWeb() || !hasWindow()) return false;

    return matchesDisplayMode('standalone') ||
        matchesDisplayMode('minimal-ui') ||
        window.navigator?.standalone === true;
};

export const getDisplayMode = () => {
    if (isNative()) return 'native';
    if (matchesDisplayMode('fullscreen')) return 'fullscreen';
    if (matchesDisplayMode('standalone')) return 'standalone';
    if (matchesDisplayMode('minimal-ui')) return 'minimal-ui';
    return 'browser';
};

export const supportsAddToHomeScreen = () => {
    if (!isWeb() || !hasWindow() || !hasNavigator()) return false;

    const userAgent = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isAndroid = /Android/.test(userAgent);
    const isChrome = /Chrome/.test(userAgent);

    return (isIOS && isSafari) || (isAndroid && isChrome);
};

export const addDisplayModeListener = (callback) => {
    if (!isWeb() || !hasWindow() || typeof window.matchMedia !== 'function') {
        return () => {};
    }

    const queries = [
        window.matchMedia('(display-mode: standalone)'),
        window.matchMedia('(display-mode: browser)'),
        window.matchMedia('(display-mode: fullscreen)'),
        window.matchMedia('(display-mode: minimal-ui)')
    ];
    const handleChange = () => callback(getDisplayMode());

    queries.forEach((query) => {
        if (typeof query.addEventListener === 'function') {
            query.addEventListener('change', handleChange);
        } else {
            query.addListener(handleChange);
        }
    });

    return () => {
        queries.forEach((query) => {
            if (typeof query.removeEventListener === 'function') {
                query.removeEventListener('change', handleChange);
            } else {
                query.removeListener(handleChange);
            }
        });
    };
};

export const runtime = Object.freeze({
    platform: getPlatform,
    getPlatform,
    isWeb,
    isNative,
    isStandalone,
    getDisplayMode,
    supportsAddToHomeScreen,
    addDisplayModeListener
});

export default runtime;
